import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

import DropDown from 'src/components/common/field/DropDown';
import InputDatePickerRange from 'src/components/common/field/InputDatePickerRange';
import Record from 'src/components/common/table/Record';
import DialogForm from 'src/components/reservationList/DialogForm';
import Pagin from 'src/components/common/Pagin';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import BreadcrumbsCustom from 'src/components/common/BreadcrumbsCustom';
import DialogDetail from 'src/components/reservationList/DialogDetail';

import { getToken } from 'src/utils/auth';
import { getAPI, postAPI } from 'src/utils/api';
import {
  BaseDataType,
} from 'src/components/reservationList/TypeCustom';
import { ItemType } from 'src/components/common/field/TypeCustom';
import { getDetailPagin } from 'src/common/helper';
import { FieldTableType } from 'src/components/common/table/TypeCustom';
import { getField } from 'src/common/helper';

const sxlabelDropdown = {
  '&.Mui-focused': {
    color: '#AAB2BD',
    transform: 'translate(14px, 8px) scale(1)',
  },
  '&.MuiInputLabel-root': {
    color: '#AAB2BD',
    transform: 'translate(14px, 8px) scale(1)',
  },
};

const sizeDropdown = {
  width: '160px',
  height: '32px',
};

const currentDate = dayjs();

const defaultFilter = {
  month: currentDate.format('YYYY-MM'),
  doctor: null,
  startDate: null,
  endDate: null,
  status: null,
  page: 1,
  pageSize: 10,
};

const fieldsDefine: FieldTableType[] = [
  {
    label: '患者ID',
    field: 'patientno',
    align: 'left',
    width: '12%'
  },
  {
    label: '患者名',
    field: 'patient',
    align: 'left',
    width: '15%'
  },
  {
    label: '予約日',
    field: 'thedate',
    align: 'left',
    width: '13%'
  },
  {
    label: '申込日',
    field: 'no',
    align: 'left',
    width: '10%'
  },
  {
    label: '医師',
    field: 'doctor',
    align: 'left',
    width: '10%'
  },
  {
    label: '予約タイプ',
    field: 'reservetype',
    align: 'left',
    width: '15%'
  },
  {
    label: '希望施術',
    field: 'parts',
    align: 'left',
    width: '15%'
  },
  {
    label: 'ステータス',
    field: 'status',
    align: 'left',
    width: '10%',
    isStatus: true
  }
];

const breadcrumbsDefine = [
  {
    to: '/',
    label: '会計'
  },
  {
    label: '予約一覧'
  }
];

const DataDialogDefine = {
  id: 0,
  patientid: 0,
  open: false
};

const DataDialogFormDefine = {
  data: undefined,
  open: false
};

type DataDialogType = {
  id: number;
  patientid: number;
  open: boolean;
};

type FilterType = {
  month: string | null;
  doctor: number | null;
  startDate: string | null;
  endDate: string | null;
  status: number | null;
  page: number | null;
  pageSize: number | null;
};

const ReservationList = () => {
  const [filter, setFilter] = useState<FilterType>(defaultFilter);

  const [dataTable, setDataTable] = useState<any[]>([]);
  const [dataDialogDetail, setDataDialogDetail] = useState<DataDialogType>(DataDialogDefine);
  const [dataDialogForm, setDataDialogForm] = useState<{
    open: boolean,
    data: any | undefined
  }>(DataDialogFormDefine);

  const [doctorData, setDoctorData] = useState<ItemType[]>([]);
  const [statusData, setStatusData] = useState<ItemType[]>([]);
  const [monthsData, setmonthsData] = useState<ItemType[]>([]);
  const [pagination, setPagination] = useState<any>({});
  const [baseData, setBaseData] = useState<BaseDataType>();
  const [loadingDataTable, setLoadingDataTable] = useState(false);

  const getReserveList = async (page: number = 1) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      setLoadingDataTable(true);

      const data = {
        month: filter.month,
        doctorid: Number(filter.doctor),
        dates:
          filter.startDate && filter.endDate
            ? `${filter.startDate} ~ ${filter.endDate}`
            : null,
        status: Number(filter.status),
        page: page,
        pageSize: filter.pageSize,
      };

      postAPI(`/reserve/search?version=v2.0&token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            setDataTable(res.data.items);
            setDoctorData(convertItemType(res.data.doctors, 'id', 'name'));
            setmonthsData(res.data.months);
            setStatusData(convertItemType(res.data.statuses, 'value', 'label'));
            setPagination(res.data.pagination);
          }
        })
        .finally(() => {
          setLoadingDataTable(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getBaseData = async () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      getAPI(`/reserve/base-data?token=${token}`)
        .then((res: any) => {
          if (res.code === 200) {
            setBaseData((prevState) => ({
              ...prevState,
              doctors: [...convertItemType(res.data.doctors, 'id', 'name')],
              ops: [...convertItemType(res.data.ops, 'id', 'category')],
              status: [...convertItemType(res.data.status, 'value', 'label')],
              part_categories: [
                ...convertItemType(res.data.part_categories, 'id', 'category'),
              ],
              parts: [...convertItemTypePart(res.data.parts)],
              machines: [...convertItemType(res.data.machines, 'id', 'name')],
              users: [...convertItemType(res.data.users, 'userid', 'name')],
            }));
          }
        })
        .finally(() => { });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const convertItemTypePart = (data: any[]) => {
    const converted = data
      ? data.map((item: any) => ({
        id: String(item.id),
        name: item.part,
        parentId: item.categoryid,
      }))
      : [];

    return converted;
  };

  const handleCloseDialogForm = (isRefreshList?: boolean) => {
    setDataDialogForm({
      ...DataDialogFormDefine
    });

    if (isRefreshList === true) {
      getReserveList();
    }
  };

  const handleOpenDialogForm = (data: any | undefined) => {
    if (dataDialogDetail.open) {
      handleCloseDialogDetail();
    };
    
    setDataDialogForm({
      data,
      open: true
    });
  };

  const handleClickRow = (data: any) => {
    setDataDialogDetail({
      id: getField(data, 'id', 0),
      patientid: getField(data, 'patientid', 0),
      open: true
    })
  };

  const handleCloseDialogDetail = (isRefreshList?: boolean) => {
    setDataDialogDetail({
      ...DataDialogDefine
    });
  };

  const convertItemType = (data: any[], id: string, name: string) => {
    const converted = data
      ? data.map((item: any) => ({
        id: String(item[id]),
        name: item[name],
      }))
      : [];

    return converted;
  };

  const handleChangeFilter = (name: string) => (id: number | string) => {
    setFilter((prevState) => ({
      ...prevState,
      [name]: id !== '' ? id : null,
    }));
  };

  const handleChangeDateRange = (
    startDate: string | null,
    endDate: string | null
  ) => {
    setFilter((prevState) => ({
      ...prevState,
      startDate: startDate,
      endDate: endDate,
    }));
  };

  const handleSearch = () => {
    getReserveList();
  };

  

  const handlePageChange = (eventL: any, value: number) => {
    getReserveList(value);
  };

  useEffect(() => {
    setFilter(defaultFilter);
    getReserveList();

    getBaseData();
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <BreadcrumbsCustom breadcrumbs={breadcrumbsDefine} />
      <Typography
        component='label'
        sx={{
          fontWeight: '500',
          fontSize: '20px',
          lineHeight: '24px',
        }}
      >
        予約
      </Typography>
      <Stack
        direction={'row'}
        spacing='8px'
        sx={{
          mt: '18px',
        }}
      >
        {!loadingDataTable ? (
          <DropDown
            selected={Number(filter.doctor)}
            idSelect='doctor-select'
            defaultSelect={'選択する'}
            placeholder='医師'
            items={doctorData}
            onChange={handleChangeFilter('doctor')}
            sxInput={sizeDropdown}
            sxLabel={sxlabelDropdown}
          />
        ) : (
          <Box width={'160px'}>
            <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
          </Box>
        )}

        {!loadingDataTable ? (
          <DropDown
            selected={filter.month}
            idSelect='month-select'
            defaultSelect={'選択する'}
            placeholder='月'
            items={monthsData}
            onChange={handleChangeFilter('month')}
            sxInput={sizeDropdown}
            sxLabel={sxlabelDropdown}
          />
        ) : (
          <Box width={'160px'}>
            <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
          </Box>
        )}

        {!loadingDataTable ? (
          <InputDatePickerRange
            startDate={filter.startDate}
            endDate={filter.endDate}
            placeholder='期間'
            sxInput={{ ...sizeDropdown, width: '240px' }}
            onChange={handleChangeDateRange}
          />
        ) : (
          <Box width={'240px'}>
            <Skeleton variant='rectangular' width={'240px'} height={'32px'} />
          </Box>
        )}

        {!loadingDataTable ? (
          <DropDown
            selected={0}
            idSelect='user-select'
            defaultSelect={'選択する'}
            placeholder='予約登録者'
            onChange={handleChangeFilter('user')}
            sxInput={sizeDropdown}
            sxLabel={sxlabelDropdown}
          />
        ) : (
          <Box width={'160px'}>
            <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
          </Box>
        )}

        {!loadingDataTable ? (
          <DropDown
            selected={filter.status}
            idSelect='status-select'
            defaultSelect={'選択する'}
            placeholder='ステータス'
            items={statusData}
            onChange={handleChangeFilter('status')}
            sxInput={sizeDropdown}
            sxLabel={sxlabelDropdown}
          />
        ) : (
          <Box width={'160px'}>
            <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
          </Box>
        )}

        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          width={'100%'}
        >
          {!loadingDataTable ? (
            <BtnGradient
              text='検索'
              handleButtonClick={handleSearch}
              sx={{
                minWidth: '60px',
                width: '60px',
              }}
            />
          ) : (
            <Box width={'60px'}>
              <Skeleton variant='rectangular' width={'60px'} height={'32px'} />
            </Box>
          )}

          {!loadingDataTable ? (
            <BtnOutlineGradient
              text='新規作成'
              srcStartIcon='/images/ic-plus-blue.svg'
              handleButtonClick={() => handleOpenDialogForm(undefined)}
              sx={{
                width: '120px',
              }}
            />
          ) : (
            <Box width={'120px'}>
              <Skeleton variant='rectangular' width={'120px'} height={'32px'} />
            </Box>
          )}
        </Stack>
      </Stack>
      <Box sx={{ mt: '27px' }}>
        {!loadingDataTable ? (
          <Record
            fields={fieldsDefine}
            items={dataTable}
            statuses={statusData}
            onClickRow={handleClickRow}
          />
        ) : (
          <Skeleton variant='rounded' width={'100%'} height={'400px'} />
        )}
        {!loadingDataTable && dataTable.length > 0 && pagination && (
          <Box display={'flex'} justifyContent={'right'} mt={'10px'}>
            <Typography
              component='label'
              sx={{
                color: '#AAB2BD',
                fontWeight: '400',
                fontSize: '10px',
                lineHeight: '12px',
              }}
            >
              {getDetailPagin(pagination)}
            </Typography>
          </Box>
        )}
        {!loadingDataTable &&
          dataTable.length > 0 &&
          pagination &&
          pagination.page && (
            <Pagin
              handlePageChange={handlePageChange}
              pagination={pagination}
            />
          )}
      </Box>
      <DialogForm
        open={dataDialogForm.open}
        data={dataDialogForm.data}
        baseData={baseData}
        onClose={handleCloseDialogForm}
      />

      <DialogDetail
        open={dataDialogDetail.open}
        patientId={dataDialogDetail.patientid}
        id={dataDialogDetail.id}
        onClose={handleCloseDialogDetail}
        onEdit={handleOpenDialogForm}
      />
    </Box>
  );
};

export default ReservationList;
